import { streemLogger } from '../util/streem_logger';
import { invariant, startAnimationLoop, VideoElementManager } from '@streem/toolbox';
import { BehaviorSubject, throttleTime, distinctUntilChanged } from 'rxjs';
import { convertPreferredVideoCodecsToVideoCodecCapabilities } from '../util/chime_codec_helper';
import { FRAME_RATE, VIDEO_HEIGHT, VIDEO_WIDTH } from '../constants';
export class ChimeVideoTrack {
    constructor(audioVideo, externalUserId) {
        this.startedSubject = new BehaviorSubject(false);
        this.enabledSubject = new BehaviorSubject(false);
        this.backgroundedSubject = new BehaviorSubject(false);
        this.kind = 'videoTrack';
        this.enabled = this.enabledSubject.asObservable();
        this.started = this.startedSubject.asObservable();
        this.backgrounded = this.backgroundedSubject.asObservable();
        this.logger = streemLogger.extend('sdk-chime-video:ChimeVideoTrack');
        this.videoElement = document.createElement('video');
        this.buffer = document.createElement('canvas');
        this.videoElementManager = new VideoElementManager(this.videoElement);
        this.audioVideo = audioVideo;
        this.externalUserId = externalUserId;
    }
    get videoTrackElement() {
        return this.videoElement;
    }
    scheduleRenderingCallback(callback) {
        const stopAnimationLoop = startAnimationLoop(() => {
            // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState
            if (this.videoElement.readyState < this.videoElement.HAVE_CURRENT_DATA) {
                this.logger.info(`Video element is not in a readyState: ${this.videoElement.readyState}`);
                return;
            }
            // Sanity check that our video has some resolution greater than 0x0.
            if (this.videoElement.videoHeight <= 0 || this.videoElement.videoWidth <= 0) {
                this.logger.info('Video element is 0x0px', {
                    width: this.videoElement.videoWidth,
                    height: this.videoElement.videoHeight,
                });
                return;
            }
            if (this.buffer.width !== this.videoElement.videoWidth ||
                this.buffer.height !== this.videoElement.videoHeight) {
                this.buffer.width = this.videoElement.videoWidth;
                this.buffer.height = this.videoElement.videoHeight;
            }
            callback(this.videoElement);
        }, this.videoElement);
        return () => {
            stopAnimationLoop();
        };
    }
    setBackgrounded(backgrounded) {
        this.logger.debug(`Remote video track backgrounded changed to ${backgrounded}`);
        this.backgroundedSubject.next(backgrounded);
    }
}
class LocalAudioVideoObserverImpl {
    constructor(localVideoElement, audioVideo) {
        this.localVideoElement = localVideoElement;
        this.audioVideo = audioVideo;
        this.currentTileId = undefined;
        this.logger = streemLogger.extend('sdk-chime-video:LocalAudioVideoObserver');
    }
    videoTileDidUpdate(tileState) {
        invariant(tileState.tileId, 'Tile id is not available');
        if (tileState.localTile && !tileState.boundVideoElement) {
            this.audioVideo.bindVideoElement(tileState.tileId, this.localVideoElement);
            this.currentTileId = tileState.tileId;
            this.logger.info(`Local video tile updated: ${tileState.tileId}`);
        }
    }
    videoTileWasRemoved(tileId) {
        if (tileId === this.currentTileId) {
            this.logger.debug(`Local video tile removed: ${tileId}`);
            this.audioVideo.unbindVideoElement(tileId);
            this.currentTileId = undefined;
        }
    }
}
export class ChimeLocalVideoTrack extends ChimeVideoTrack {
    constructor(audioVideo, externalUserId, preferredVideoCodecs) {
        super(audioVideo, externalUserId);
        this.inputDevice = undefined;
        this.videoMediaStream = undefined;
        this.preferredVideoCodecs = preferredVideoCodecs;
    }
    getMediaTrackConstraints() {
        invariant(this.videoMediaStream, 'Video media stream is not available');
        return this.videoMediaStream.getVideoTracks()[0].getConstraints();
    }
    async restart(constraints) {
        this.logger.info(`Restarting local video track trackLabel=${this.trackLabel}`);
        invariant(constraints, 'constraints should not be undefined');
        try {
            if (this.videoMediaStream) {
                this.videoMediaStream.getVideoTracks().forEach(videoTrack => {
                    videoTrack.stop();
                });
            }
            this.logger.info(`Restarting video with constraints: ${JSON.stringify(constraints)}`);
            this.videoMediaStream = await this.audioVideo.startVideoInput(constraints);
            invariant(this.videoMediaStream, 'Video media stream is not available after restart');
            const videoTracks = this.videoMediaStream.getVideoTracks();
            invariant(videoTracks.length > 0, 'Video media stream is not available after attempting restart');
            this.trackLabel = videoTracks[0].label;
            this.logger.info(`Local video track restarted with settings: ${JSON.stringify(videoTracks[0].getSettings())}`);
            return true;
        }
        catch (error) {
            this.logger.error(`Failed to restart local video track ${this.trackLabel}`, error);
            return false;
        }
    }
    setEnabled(enable) {
        if (enable) {
            this.audioVideo.startLocalVideoTile();
            this.enabledSubject.next(true);
        }
        else {
            this.audioVideo.stopLocalVideoTile();
            this.enabledSubject.next(false);
        }
    }
    async initializeVideoTrack(videoConstraints, videoInputDevices) {
        invariant(videoConstraints, 'videoConstraints should not be false or undefined');
        if (this.audioVideo.setVideoCodecSendPreferences) {
            const videoCapabilities = convertPreferredVideoCodecsToVideoCodecCapabilities(this.preferredVideoCodecs);
            this.logger.info('Setting preferred video codecs', JSON.stringify(videoCapabilities));
            this.audioVideo.setVideoCodecSendPreferences(videoCapabilities);
        }
        this.audioVideo.chooseVideoInputQuality(VIDEO_WIDTH, VIDEO_HEIGHT, FRAME_RATE);
        this.logger.info('Input quality has been set to:', this.audioVideo.getVideoInputQualitySettings());
        this.inputDevice =
            videoConstraints === true
                ? (await this.findDefaultCamera(videoInputDevices)).deviceId
                : videoConstraints;
        this.logger.info(`Initializing video with constraints: ${JSON.stringify(this.inputDevice)}`);
        this.videoMediaStream = await this.audioVideo.startVideoInput(this.inputDevice);
        invariant(this.videoMediaStream, 'Video media stream is not available after initialization');
        this.startedSubject.next(true);
        this.trackLabel = this.videoMediaStream.getVideoTracks()[0].label;
        this.localAudioVideoObserver = new LocalAudioVideoObserverImpl(this.videoElement, this.audioVideo);
        this.audioVideo.addObserver(this.localAudioVideoObserver);
        this.videoElementManager.addHiddenVideoToDOM();
        this.logger.info(`LocalChimeVideoTrack with label=${this.trackLabel} created local video for user with userId: ${this.externalUserId} with settings: ${JSON.stringify(this.videoMediaStream.getVideoTracks()[0].getSettings())}`);
        return true;
    }
    async disconnect() {
        this.logger.info('ChimeLocalVideoTrack disconnecting the local video track');
        this.videoElementManager.removeVideoFromDOM();
        this.audioVideo.stopLocalVideoTile();
        this.audioVideo.removeLocalVideoTile();
        await this.audioVideo.stopVideoInput();
        this.startedSubject.next(false);
        this.enabledSubject.next(false);
        this.audioVideo.removeObserver(this.localAudioVideoObserver);
        this.trackLabel = '';
        this.inputDevice = undefined;
        this.videoMediaStream = undefined;
    }
    async findDefaultCamera(videoInputDevices) {
        invariant(videoInputDevices.length > 0, 'No video input devices found');
        const defaultDevice = videoInputDevices.find(device => device.deviceId === 'default');
        if (defaultDevice) {
            return defaultDevice;
        }
        this.logger.warn('No default video input device found, using first device');
        return videoInputDevices[0];
    }
}
export class ChimeRemoteVideoTrack extends ChimeVideoTrack {
    constructor(audioVideo, externalUserId) {
        super(audioVideo, externalUserId);
        this.pausedSubject = new BehaviorSubject(false);
        this.paused = this.pausedSubject
            .asObservable()
            .pipe(distinctUntilChanged(), throttleTime(3000, undefined, { trailing: true, leading: false }));
        this.onPausedChange = (paused) => {
            this.logger.debug(`Remote video track paused changed to ${paused}`);
            this.pausedSubject.next(paused);
        };
        this.onEnabledChange = (enabled) => {
            this.logger.debug(`Remote video track enabled changed to ${enabled}`);
            this.enabledSubject.next(enabled);
        };
    }
    initializeVideoTrack() {
        this.videoElementManager.addHiddenVideoToDOM();
        this.startedSubject.next(true);
        return true;
    }
    disconnect() {
        this.logger.info('ChimeRemoteVideoTrack disconnecting the remote video track');
        this.videoElementManager.removeVideoFromDOM();
        this.enabledSubject.next(false);
        this.startedSubject.next(false);
        this.trackLabel = '';
    }
}
//# sourceMappingURL=video_tracks.js.map